/**
 * <oppie-sticky-atc> — fixed bottom add-to-cart bar (Smooche-style).
 *
 * Relocated to <body> on connect so position:fixed is relative to the viewport
 * (Horizon section wrappers are position:relative). Watches the MAIN buy-box
 * add-to-cart button with an IntersectionObserver: the bar reveals when that
 * button is OUT of view and hides while it is visible. Near the page bottom
 * (footer in view) the bar also hides so it never overlaps the footer CTA.
 *
 * The CTA does NOT build its own /cart/add — it programmatically clicks the
 * real buy-box button so Horizon's existing AJAX add + cart-drawer open is
 * reused, fully respecting the chosen variant and quantity. Falls back to a
 * no-op (stays hidden) when the main button can't be found.
 */
class OppieStickyAtc extends HTMLElement {
  connectedCallback() {
    if (this.dataset.relocated !== 'true' && document.body && this.parentElement !== document.body) {
      this.dataset.relocated = 'true';
      document.body.appendChild(this);
      return; // re-runs connectedCallback after the move
    }

    this.mainAtc = this.#findMainAtc();
    if (!this.mainAtc) {
      this.hidden = true;
      return;
    }

    this.cta = this.querySelector('[data-sticky-add]');
    this.mainVisible = true;
    this.atFoot = false;
    this.barShown = false;

    this.#initObservers();
    this.#initClick();
    this.#initStateSync();
    this.#watchChat();
  }

  disconnectedCallback() {
    this.atcObserver?.disconnect();
    this.footObserver?.disconnect();
    this.stateObserver?.disconnect();
    this.chatObserver?.disconnect();
    this.#toggleChat(false); // restore the chat widget when the bar leaves the DOM
  }

  #findMainAtc() {
    const selectors = (this.dataset.mainAtc || "button[name='add']").split(',');
    for (const raw of selectors) {
      const selector = raw.trim();
      if (!selector) continue;
      const el = document.querySelector(selector);
      if (el) return el;
    }
    return null;
  }

  #initObservers() {
    this.atcObserver = new IntersectionObserver(
      (entries) => {
        this.mainVisible = entries.some((entry) => entry.isIntersecting);
        this.#sync();
      },
      { threshold: 0 }
    );
    this.atcObserver.observe(this.mainAtc);

    // Hide near the page bottom so the bar never sits over the footer.
    const footer = document.querySelector('footer, .footer, .shopify-section--footer');
    if (footer) {
      this.footObserver = new IntersectionObserver(
        (entries) => {
          this.atFoot = entries.some((entry) => entry.isIntersecting);
          this.#sync();
        },
        { threshold: 0 }
      );
      this.footObserver.observe(footer);
    }
  }

  #sync() {
    const show = !this.mainVisible && !this.atFoot;
    this.barShown = show;
    this.hidden = !show;
    this.classList.toggle('is-visible', show);
    this.#toggleChat(show);
  }

  /**
   * The chat launcher is injected — and sometimes re-created — as a direct
   * child of <body> AFTER the bar's first sync. On mobile the bar can be up at
   * rest, so without watching, the chat would stay visible. A persistent
   * childList observer on <body> (cheap, no subtree) re-applies the current bar
   * state whenever the launcher is added or replaced; #sync handles the rest.
   */
  #watchChat() {
    this.chatObserver = new MutationObserver(() => this.#toggleChat(this.barShown));
    this.chatObserver.observe(document.body, { childList: true });
    this.#toggleChat(this.barShown); // apply to a launcher already present
  }

  /**
   * Hide the Shopify chat launcher while the sticky bar is up so the two don't
   * collide bottom-right; restore it the moment the bar hides. Shopify has
   * shipped the launcher under several names over time: the legacy Inbox
   * element (id `shopify-chat`), the Storekick widget (<shopify-agent>), and
   * the current <shopify-chat> custom element (a bare <body> child, no id), so
   * we match whichever is present. Re-queried each sync because the widget
   * loads async. We only ever touch our own `display` override — the widget
   * sets no inline display of its own, so removeProperty cleanly hands control
   * back.
   */
  #toggleChat(barVisible) {
    const chat =
      document.querySelector('shopify-agent, shopify-chat') ||
      document.getElementById('shopify-chat');
    if (!chat) return;
    if (barVisible) {
      chat.style.setProperty('display', 'none', 'important');
    } else {
      chat.style.removeProperty('display');
    }
  }

  #initClick() {
    if (!this.cta) return;
    this.cta.addEventListener('click', (event) => {
      event.preventDefault();
      // Re-resolve in case Horizon re-rendered the form (variant switch).
      const target = this.#findMainAtc() || this.mainAtc;
      if (target && !target.disabled) {
        target.click();
      }
    });
  }

  /**
   * Mirror the MAIN buy-box button's availability onto the sticky CTA so the
   * bar shows the same sold-out state as the main and bottom-section buttons.
   * The main button is the single source of truth: when it's disabled (sold
   * out / unavailable for the chosen variant) the sticky CTA is disabled and
   * swapped to the "Uitverkocht" label; otherwise it shows its own short label.
   * Horizon flips the main button's `disabled` in place on variant switch, so a
   * MutationObserver on it keeps the sticky CTA in sync.
   */
  #initStateSync() {
    if (!this.cta || !this.mainAtc) return;
    this.stateObserver = new MutationObserver(() => this.#syncCtaState());
    this.stateObserver.observe(this.mainAtc, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
    });
    this.#syncCtaState();
  }

  #syncCtaState() {
    const main = this.#findMainAtc() || this.mainAtc;
    if (!main || !this.cta) return;

    const soldOut = main.disabled || main.getAttribute('aria-disabled') === 'true';
    this.cta.disabled = soldOut;

    const label = soldOut ? this.cta.dataset.labelSoldout : this.cta.dataset.labelDefault;
    if (label && this.cta.textContent.trim() !== label) {
      this.cta.textContent = label;
    }
  }
}

if (!customElements.get('oppie-sticky-atc')) {
  customElements.define('oppie-sticky-atc', OppieStickyAtc);
}
